import { ofType } from 'redux-observable';
import { timer, Observable } from 'rxjs';
import {
   tap,
   takeUntil,
   concatMap,
   // timeInterval,
} from 'rxjs/operators';

import { mixer } from './mixer';
import {
   SET_REFERENCE_PLAYING,
   SET_USER_PLAYING,
   STOP_PLAYING,
   TOGGLE_REFERENCE_PLAYING,
   TOGGLE_USER_PLAYING,
} from './actions';
import { audioContext } from '../../webAudio/audioContext';
import { createSynthNode } from '../../webAudio/createSynthNode';
import { lastLoopStart } from './lastLoopStart';
import {
   CLEAR_ACTIVE_INTERFACE,
   SET_ACTIVE_INTERFACE,
} from '../../app/actions';

const getPartDuration = (currentPart, partsPerBeat, tempo, swing) => {
   const partDurationWithoutSwing = 60 / tempo / partsPerBeat;
   const partsPerStep = partsPerBeat / 4;
   const currentStep = Math.floor(currentPart / partsPerStep);
   const swingOffset = (swing / 2) * (60 / tempo / partsPerBeat);

   if (currentStep % 2 === 0) {
      return partDurationWithoutSwing + swingOffset;
   }
   return partDurationWithoutSwing - swingOffset;
};

const getNoteEnd = (
   startPart,
   startTime,
   noteLength,
   partsPerBeat,
   tempo,
   swing
) => {
   const partsPerStep = partsPerBeat / 4;
   const partsPerHalfBeat = partsPerBeat / 2;
   const halfBeatsInLength = Math.floor(noteLength / partsPerHalfBeat);
   const remainingParts = noteLength % partsPerHalfBeat;
   const partDurationWithoutSwing = 60 / tempo / partsPerBeat;

   if (remainingParts === 0) {
      return startTime + noteLength * partDurationWithoutSwing;
   }

   const startStep = Math.floor(startPart / partsPerStep);

   const partsInStartStep = Math.min(
      remainingParts,
      partsPerStep - (startPart % partsPerStep)
   );
   const partsInNextStep = Math.max(remainingParts - partsInStartStep, 0);

   const swingOffset = (swing / 2) * (60 / tempo / partsPerBeat);

   if (startStep % 2 === 0) {
      return (
         startTime +
         halfBeatsInLength * 2 * partsPerStep * partDurationWithoutSwing +
         partsInStartStep * (partDurationWithoutSwing + swingOffset) +
         partsInNextStep * (partDurationWithoutSwing - swingOffset)
      );
   }
   return (
      startTime +
      halfBeatsInLength * 2 * partsPerStep * partDurationWithoutSwing +
      partsInStartStep * (partDurationWithoutSwing - swingOffset) +
      partsInNextStep * (partDurationWithoutSwing + swingOffset)
   );
};

const playSequence = (action$, state$) =>
   Observable.create((observer) => {
      let currentPart = 0;
      let nextPartTime;

      const noteSet = new Set();

      if (process.env.NODE_ENV === 'development') {
         window.sequencerNoteSet = noteSet;
      }
      const stopNotes = () => {
         noteSet.forEach((node) => node.stop());
      };

      const nextParts = () =>
         Observable.create((npObserver) => {
            const {
               lookahead,
               tempo,
               referenceTempo,
               referencePlaying,
               partsPerBeat,
               beatCount,
               swing,
               referenceSwing,
            } = state$.value.activeInterface;

            const tempoToUse = referencePlaying ? referenceTempo : tempo;
            const swingToUse = referencePlaying ? referenceSwing : swing;
            const now = audioContext.currentTime;
            if (nextPartTime == null) {
               nextPartTime = now;
            }

            // while there are parts that will need to play before the next interval,
            // schedule them and advance the pointer.
            while (nextPartTime < now + lookahead) {
               if (currentPart === 0) {
                  lastLoopStart.previousScheduledTime =
                     lastLoopStart.currentScheduledTime;
                  lastLoopStart.currentScheduledTime = nextPartTime;
               }
               // don't build up parts to play if system is slow
               if (nextPartTime >= now) {
                  npObserver.next({ part: currentPart, time: nextPartTime });
               }

               nextPartTime += getPartDuration(
                  currentPart,
                  partsPerBeat,
                  tempoToUse,
                  swingToUse
               );

               currentPart = (currentPart + 1) % (beatCount * partsPerBeat);
            }

            npObserver.complete();
         });

      const scheduleParts = ({ part, time }) => {
         const {
            tempo,
            referenceTempo,
            partsPerBeat,
            referencePlaying,
            swing,
            referenceSwing,
         } = state$.value.activeInterface;

         const tempoToUse = referencePlaying ? referenceTempo : tempo;
         const swingToUse = referencePlaying ? referenceSwing : swing;

         state$.value.activeInterface.instrumentRack.forEach(
            ({ pattern, referencePattern, source }, instrumentIndex) => {
               const patternToPlay = referencePlaying
                  ? referencePattern
                  : pattern;

               // prevent error when they reduce pattern length after it's been scheduled
               if (!patternToPlay?.[part]) {
                  return;
               }

               const sound =
                  state$.value.activeInterface.soundKits[
                     state$.value.activeInterface.soundKit?.id
                  ]?.sounds?.[source];

               // prevent error when they play before soundkit has loaded
               if (!sound) {
                  // could show warning here that sounds haven't loaded?
                  return;
               }

               patternToPlay[part].forEach(({ note, noteLength }) => {
                  const synthNode = createSynthNode(sound, note);
                  synthNode.connect(mixer.tracks[instrumentIndex].pregain);

                  synthNode.start(time);
                  if (noteLength) {
                     synthNode.stop(
                        getNoteEnd(
                           part,
                           time,
                           noteLength,
                           partsPerBeat,
                           tempoToUse,
                           swingToUse
                        )
                     );
                  }
                  noteSet.add(synthNode);
                  synthNode.onended = () => noteSet.delete(synthNode);
               });
            }
         );
      };

      const intervalTimer = timer(
         0,
         state$.value.activeInterface.intervalTime
      ).pipe(
         takeUntil(
            action$.pipe(
               ofType(
                  TOGGLE_REFERENCE_PLAYING,
                  TOGGLE_USER_PLAYING,
                  STOP_PLAYING,
                  CLEAR_ACTIVE_INTERFACE,
                  SET_ACTIVE_INTERFACE,
                  SET_USER_PLAYING,
                  SET_REFERENCE_PLAYING
               ),
               tap(stopNotes),
               tap(() => {
                  lastLoopStart.currentScheduledTime = null;
                  lastLoopStart.previousScheduledTime = null;
               })
            )
         ),
         concatMap(nextParts),
         tap(scheduleParts)
      );

      return intervalTimer.subscribe({ complete: () => observer.complete() });
   });

export { playSequence };
